/**
 * Vorlage „Gas-Pizzaofen aufheizen“.
 *
 * Ein kuppelförmiger Pizzaofen auf kurzen Beinen, daneben die Gasflasche.
 * Der Regler wird aufgedreht, die Flamme rollt hinten an der Decke entlang
 * und der Pizzastein glüht langsam auf. Ein Infrarot-Thermometer misst die
 * Steintemperatur, bis sie die Zieltemperatur erreicht.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path, roundedRectPath } from '../paths.ts';
import { circle, compact, faded, group, label, line, rect, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { flame, heatWaves } from '../parts/effects.ts';
import { arrow, counter, doneMark, durationLabel, GROUND_Y, timerBadge } from '../parts/stage.ts';
import { minutesFromText } from '../inference.ts';
import { defineTemplate, numberValue } from '../template.ts';

/** Gehäuse und Öffnung. */
const BODY = { left: 20, right: 150, top: 46, bottom: 116 } as const;
const MOUTH = { left: 46, right: 124, top: 78, bottom: 112 } as const;
const MOUTH_CX = (MOUTH.left + MOUTH.right) / 2;
/** Gasflasche. */
const BOTTLE = { cx: 210, top: 86, width: 34 } as const;
/** Thermometer: Messfenster vorn links. */
const GUN = { x: 168, y: 20 } as const;

/** Zeitplan. */
const TIME = { knob: [0.02, 0.08], heat: [0.08, 0.8], done: [0.8, 0.84], reset: [0.9, 1] } as const;

/** Umriss der Öffnung: gerade Seiten, runder Bogen oben. */
function mouthPath(): string {
  return path()
    .move(MOUTH.left, MOUTH.bottom)
    .line(MOUTH.left, MOUTH.top + 12)
    .curve(MOUTH.left, MOUTH.top - 4, MOUTH.right, MOUTH.top - 4, MOUTH.right, MOUTH.top + 12)
    .line(MOUTH.right, MOUTH.bottom)
    .close()
    .toString();
}

/** Gehäuse mit gewölbter Oberseite und kurzen Beinen. */
function ovenBody(): SceneNode[] {
  const body = path()
    .move(BODY.left, BODY.bottom)
    .line(BODY.left, BODY.top + 26)
    .curve(BODY.left, BODY.top - 6, BODY.right, BODY.top - 6, BODY.right, BODY.top + 26)
    .line(BODY.right, BODY.bottom)
    .close()
    .toString();
  return [
    // Beine
    rect(BODY.left + 12, BODY.bottom, 7, GROUND_Y - BODY.bottom, 2, styles.solid('ink')),
    rect(BODY.right - 19, BODY.bottom, 7, GROUND_Y - BODY.bottom, 2, styles.solid('ink')),
    shape(body, styles.solid('paper')),
    // Zierlinie entlang der Kuppel
    shape(
      path()
        .move(BODY.left + 10, BODY.top + 30)
        .curve(BODY.left + 12, BODY.top + 4, BODY.right - 12, BODY.top + 4, BODY.right - 10, BODY.top + 30)
        .toString(),
      styles.fine('shade'),
    ),
    // Sockelleiste
    rect(BODY.left - 4, BODY.bottom - 6, BODY.right - BODY.left + 8, 8, 3, styles.solid('shade')),
  ];
}

/** Gasflasche mit Ventil und Schlauch zum Ofen. */
function bottle(): SceneNode[] {
  const { cx, top, width } = BOTTLE;
  return [
    shape(
      path()
        .move(cx, top - 8)
        .curve(cx + 18, top - 8, BODY.right + 20, BODY.bottom - 20, BODY.right - 4, BODY.bottom - 18)
        .toString(),
      { stroke: 'ink', strokeWidth: STROKE.outline },
    ),
    shape(roundedRectPath(cx - width / 2, top, width, GROUND_Y - top, 12), styles.solid('tint')),
    line(cx - width / 2 + 3, top + 18, cx + width / 2 - 3, top + 18, styles.fine('shade')),
    rect(cx - 6, top - 10, 12, 11, 2, styles.solid('ink')),
    rect(cx - 10, top - 13, 20, 4, 2, styles.solid('deep')),
  ];
}

/** Infrarot-Thermometer mit Anzeige; zeigt nach links unten auf den Stein. */
function thermometer(temperature: number, reached: boolean): SceneNode[] {
  const { x, y } = GUN;
  return [
    // Griff
    group([shape(roundedRectPath(x + 40, y + 18, 13, 30, 4), styles.solid('ink'))], {
      transform: { rotate: 14, ox: x + 46, oy: y + 18 },
    }),
    shape(roundedRectPath(x, y, 68, 24, 6), styles.solid('paper')),
    // Messkopf
    shape(roundedRectPath(x - 8, y + 6, 10, 12, 3), styles.solid('ink')),
    rect(x + 6, y + 4, 56, 16, 3, { fill: 'cavity', stroke: 'deep', strokeWidth: STROKE.fine }),
    label(x + 34, y + 17, `${Math.round(temperature)} °C`, 13, {
      fill: reached ? 'accent' : 'snow',
      weight: 800,
    }),
  ];
}

/** Vorlage „Gas-Pizzaofen aufheizen“. */
export const bakePizzaOvenTemplate = defineTemplate({
  id: 'bake.pizza-oven',
  group: 'bake',
  title: 'Gas-Pizzaofen aufheizen',
  description:
    'Der Gas-Pizzaofen wird auf volle Flamme gestellt, bis der Pizzastein die Zieltemperatur erreicht. Gemessen wird mit dem Infrarot-Thermometer in der Mitte des Steins.',
  durationSeconds: 6,
  keywords: [
    'pizzaofen',
    'gas pizzaofen',
    'gaspizzaofen',
    'pizzastein',
    'aufheizen',
    'pizzaofen vorheizen',
    'pizzaofen aufheizen',
    'infrarot',
    'steintemperatur',
  ],
  stillTime: 0.86,
  parameters: [
    {
      kind: 'number',
      name: 'temperature',
      label: 'Steintemperatur',
      min: 250,
      max: 500,
      step: 10,
      unit: '°C',
      defaultValue: 400,
    },
    {
      kind: 'number',
      name: 'minutes',
      label: 'Aufheizzeit',
      min: 5,
      max: 60,
      step: 5,
      unit: 'Min',
      defaultValue: 20,
    },
  ],
  inferValues(text) {
    const degrees = /(\d{3})\s*(?:°|grad)/i.exec(text);
    return {
      temperature: degrees?.[1] === undefined ? undefined : Number(degrees[1]),
      minutes: minutesFromText(text),
    };
  },
  scene(values, t) {
    const target = numberValue(values, 'temperature', 400);
    const minutes = numberValue(values, 'minutes', 20);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const on = ease.inOutCubic(segment(t, ...TIME.knob)) * (1 - reset);
    const heat = ease.inOutSine(segment(t, ...TIME.heat)) * (1 - reset);
    const reached = t >= TIME.done[0] && t < TIME.reset[0];
    const temperature = Math.round(lerp(20, target, heat) / 10) * 10;
    const done = ease.outBack(segment(t, ...TIME.done)) * (1 - segment(t, 0.88, 0.9));

    // Flammenwand hinten an der Decke: liegende Flammen, die nach vorn züngeln.
    const flames: SceneNode[] = [];
    if (on > 0.02) {
      [0, 1, 2, 3].forEach((index) => {
        const size = (14 + index * 3) * (0.5 + 0.5 * on);
        flames.push(
          group([flame(MOUTH.left + 2, MOUTH.top + 10 + index * 5, size, t, index * 1.7)], {
            transform: { rotate: 90, ox: MOUTH.left + 2, oy: MOUTH.top + 10 + index * 5 },
          }),
        );
      });
    }
    const mouth = mouthPath();
    const stone = path()
      .move(MOUTH.left, MOUTH.bottom - 7)
      .line(MOUTH.right, MOUTH.bottom - 7)
      .line(MOUTH.right, MOUTH.bottom)
      .line(MOUTH.left, MOUTH.bottom)
      .close()
      .toString();
    // Messstrahl vom Thermometer auf die Steinmitte.
    const beamOn = heat > 0.02;

    return compact([
      ...counter(),
      ...bottle(),
      ...ovenBody(),
      shape(mouth, styles.solid('cavity')),
      group(
        [
          ...flames,
          shape(stone, styles.flat('shade')),
          shape(stone, { fill: 'accent', stroke: 'none', opacity: heat * 0.85 }),
          line(MOUTH.left, MOUTH.bottom - 7, MOUTH.right, MOUTH.bottom - 7, styles.detail()),
        ],
        { clip: mouth },
      ),
      shape(mouth, styles.outline()),
      // Gasregler mit Kontrolllampe
      circle(BODY.right - 14, BODY.top + 40, 7, styles.solid('paper')),
      group([line(BODY.right - 14, BODY.top + 38, BODY.right - 14, BODY.top + 34, styles.detail())], {
        transform: { rotate: on * 150, ox: BODY.right - 14, oy: BODY.top + 40 },
      }),
      circle(BODY.right - 14, BODY.top + 54, 2.5, { fill: on > 0.05 ? 'accent' : 'shade', stroke: 'none' }),
      ...heatWaves((BODY.left + BODY.right) / 2, BODY.top - 4, t, heat * 0.8, 3, 30),
      beamOn
        ? line(GUN.x - 8, GUN.y + 12, MOUTH_CX + 6, MOUTH.bottom - 9, {
            stroke: 'deep',
            strokeWidth: STROKE.detail,
            dash: '4 4',
          })
        : null,
      ...thermometer(temperature, reached),
      timerBadge(50, 18, durationLabel(minutes), heat, 'center'),
      faded(done, [doneMark(112, 18, 1)]),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: MOUTH.left + 6, y: MOUTH.top + 6 },
        { x: MOUTH_CX, y: MOUTH.top + 2 },
        { x: MOUTH.right - 8, y: MOUTH.top + 10 },
      ]),
    ];
  },
});
